"use client";

/**
 * SessionWatchdog (#632)
 *
 * Keeps the `rawiah.session` cookie fresh while the tab is active. Calls
 * `POST /api/auth/refresh` ~5 minutes before the access token expires,
 * and also when the tab regains visibility after being hidden long
 * enough that the access cookie is likely close to (or past) expiry.
 *
 * The endpoint is the same one server-side `getServerSession` and the
 * edge middleware use — same cookie rotation, same block-check. The
 * client doesn't need to look at the response body: success means the
 * browser already has fresh cookies, and the next page navigation will
 * pick them up.
 *
 * Why this exists when server-side + middleware refresh already cover
 * server renders: client-only SPA-style interactions (e.g. a student
 * sitting on `/dashboard/calendar` clicking around) trigger plenty of
 * authenticated API calls but no full page navigation. Without a
 * client-side ping the session would silently expire and the next API
 * call would 401, dropping them to /auth. This component closes that gap.
 *
 * Mounted unconditionally inside Providers — the refresh endpoint is a
 * cheap no-op for signed-out callers (returns 401 quickly, no DB hit).
 */

import { useEffect, useRef } from "react";
import { useSession } from "@/lib/auth-react";

/** Refresh this many ms before the access token's `exp`. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;
/** Hard floor so a freshly-issued token doesn't immediately re-refresh. */
const MIN_REFRESH_WAIT_MS = 30 * 1000;
/** If the access cookie is older than this on visibility-change, refresh. */
const VISIBILITY_REFRESH_THRESHOLD_MS = 10 * 60 * 1000;

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function refreshNow(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function SessionWatchdog() {
  const { data: session, status } = useSession();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRefreshAtRef = useRef<number>(Date.now());

  useEffect(() => {
    // Only run for authenticated sessions.
    if (status !== "authenticated") {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const accessToken = session?.accessToken;
    if (!accessToken) return;

    const exp = decodeJwtExp(accessToken);
    if (!exp) return;

    const now = Date.now();
    const fireAt = exp - REFRESH_LEAD_MS;
    const wait = Math.max(fireAt - now, MIN_REFRESH_WAIT_MS);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const ok = await refreshNow();
      if (ok) {
        lastRefreshAtRef.current = Date.now();
        // Force a server render to pick up the rotated cookies — without
        // this, the in-memory session object still carries the OLD
        // access token even though the cookie is fresh.
        if (typeof window !== "undefined") {
          // Soft reload: keeps scroll, replays the current URL through
          // middleware which will re-hydrate the session with the new
          // cookie via the layout's getServerSession.
          window.location.reload();
        }
      }
      // On failure we just let middleware/server-fetch handle the next
      // 401 — usually that means a block or an expired refresh, and
      // those need a re-OAuth anyway.
    }, wait);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [status, session?.accessToken]);

  // Visibility refresh: when the tab regains focus after being hidden,
  // proactively refresh if it's been a while since the last one.
  useEffect(() => {
    if (status !== "authenticated") return;

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const sinceLast = Date.now() - lastRefreshAtRef.current;
      if (sinceLast < VISIBILITY_REFRESH_THRESHOLD_MS) return;
      refreshNow().then((ok) => {
        if (ok) {
          lastRefreshAtRef.current = Date.now();
          window.location.reload();
        }
      });
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [status]);

  return null;
}
